import { useEffect, useState, type FormEvent } from 'react';
import { SlashMark } from '@/components/common';

/**
 * Shown when a public visitor clicks a locked section. Collects a lead for the
 * "SOC 2 from A-Z" kit. Static demo: submission is not wired to a backend yet -
 * the success state is local. Wire to a Cloud Function / CRM when the data
 * layer lands (see project notes).
 */
export function KitProposalModal({
  section,
  onClose,
}: {
  section: string;
  onClose: () => void;
}) {
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const valid =
    form.name.trim().length > 1 &&
    /\S+@\S+\.\S+/.test(form.email) &&
    form.company.trim().length > 1;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setSent(true);
  };

  const firstName = form.name.trim().split(/\s+/)[0] || 'there';

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label="Request a kit proposal"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-x" onClick={onClose} aria-label="Close">
          ×
        </button>

        {sent ? (
          <div className="modal-done">
            <div className="modal-tick">✓</div>
            <h2>Thanks, {firstName}.</h2>
            <p>
              We have your request for the <strong>SOC 2 from A-Z</strong> kit and
              will reach out at <strong>{form.email}</strong> with a tailored
              proposal: scope, timeline, and pricing.
            </p>
            <button className="btn btn-grad" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="modal-head">
              <SlashMark />
              <span className="modal-eyebrow">Kit · SOC 2 from A-Z</span>
            </div>
            <h2 className="modal-title">Request a kit proposal</h2>
            <p className="modal-sub">
              <strong>{section}</strong> is part of the full guided engagement -
              the end-to-end kit that takes you from scoping to a finished SOC 2
              report. Tell us a bit about your company and we will send a tailored
              proposal.
            </p>

            <form className="modal-form" onSubmit={submit}>
              <label className="modal-field">
                <span>Name</span>
                <input
                  value={form.name}
                  onChange={set('name')}
                  placeholder="Your name"
                  autoFocus
                />
              </label>
              <label className="modal-field">
                <span>Work email</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder="you@company.com"
                />
              </label>
              <label className="modal-field">
                <span>Company</span>
                <input
                  value={form.company}
                  onChange={set('company')}
                  placeholder="Company name"
                />
              </label>
              <label className="modal-field">
                <span>
                  What are you hoping to achieve?{' '}
                  <em style={{ color: 'var(--ink-45)', fontStyle: 'normal' }}>
                    (optional)
                  </em>
                </span>
                <textarea
                  value={form.message}
                  onChange={set('message')}
                  placeholder="Target report type, timeline, anything we should know..."
                />
              </label>

              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-grad"
                  disabled={!valid}
                  style={!valid ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                >
                  Request proposal
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
